import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  MenuToggle,
  Pagination,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { DownloadIcon, ExclamationCircleIcon, SearchIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useAdminWalletTransactions, useAdminWallets } from '../../../hooks/useAdminWallets';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatPrice } from '../../../utils/money';
import { downloadTextFile } from '../../../utils/fileDownload';
import { SignedAmount, TransactionKindLabel, transactionKindText } from '../../Organization/Billing/paymentDisplay';
import type { WalletTransactionKind } from '../../../types/models/Wallet';
import { buildLedgerCsv, ledgerCsvFilename } from './ledgerCsv';
import { parseStrictIsoDate, toApiDate } from './walletUtils';

const KINDS: WalletTransactionKind[] = ['deposit', 'charge', 'reversal', 'clawback', 'adjustment'];
const isKind = (v: unknown): v is WalletTransactionKind => typeof v === 'string' && (KINDS as string[]).includes(v);

/**
 * The cross-client ledger for a period (GET /admin/wallets/transactions) — the accountant's
 * reconciliation export. Filters fetch automatically (debounced); the CSV is built client-side from
 * every loaded row. Organizations come from the wallets list, never from /clients (refused under an
 * impersonation session).
 */
const WalletsExportTab: React.FunctionComponent<{ isStaff: boolean }> = ({ isStaff }) => {
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [to, setTo] = useState<string | undefined>(undefined);
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [kind, setKind] = useState<WalletTransactionKind | undefined>(undefined);
  const [isClientOpen, setIsClientOpen] = useState(false);
  const [isKindOpen, setIsKindOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const fromCandidate = toApiDate(fromInput);
  const toCandidate = toApiDate(toInput);
  // Commit only complete dates, after a pause, and never an inverted range's upper bound.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFrom(fromCandidate);
      setTo(fromCandidate && toCandidate && toCandidate < fromCandidate ? undefined : toCandidate);
    }, 400);
    return () => clearTimeout(handle);
  }, [fromCandidate, toCandidate]);

  useEffect(() => {
    setPage(1);
  }, [from, to, clientId, kind]);

  const { wallets } = useAdminWallets(undefined, isStaff);
  const { transactions, truncated, loading, error, refetch } = useAdminWalletTransactions(
    { from, to, client_id: clientId, kind },
    isStaff,
  );

  const paginated = useMemo(
    () => transactions.slice((page - 1) * perPage, page * perPage),
    [transactions, page, perPage],
  );

  const clientLabel =
    clientId === undefined
      ? 'All organizations'
      : (wallets.find((w) => w.client_id === clientId)?.client_name ?? `Client #${clientId}`);
  const kindLabel = kind === undefined ? 'All kinds' : transactionKindText(kind);

  const downloadCsv = () => {
    downloadTextFile(ledgerCsvFilename(from, to), buildLedgerCsv(transactions), 'text/csv;charset=utf-8');
  };

  const hasFilters = from !== undefined || to !== undefined || clientId !== undefined || kind !== undefined;
  const clearFilters = () => {
    setFromInput('');
    setToInput('');
    setClientId(undefined);
    setKind(undefined);
  };

  const pagination = (variant?: 'bottom') => (
    <Pagination
      itemCount={transactions.length}
      perPage={perPage}
      page={page}
      onSetPage={(_e, p) => setPage(p)}
      onPerPageSelect={(_e, pp) => {
        setPerPage(pp);
        setPage(1);
      }}
      variant={variant}
    />
  );

  const renderBody = () => {
    if (loading && transactions.length === 0 && !error) {
      return <CenteredSpinner />;
    }
    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the ledger"
          icon={ExclamationCircleIcon}
          status="danger"
          headingLevel="h2"
          variant="lg"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }
    if (transactions.length === 0) {
      return (
        <EmptyState titleText="No movements" icon={SearchIcon} headingLevel="h2" variant="lg">
          <EmptyStateBody>
            {hasFilters ? 'No ledger movement matches the current period and filters.' : 'The ledger is empty.'}
          </EmptyStateBody>
          {hasFilters && (
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={clearFilters}>
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          )}
        </EmptyState>
      );
    }
    return (
      <>
        {truncated && (
          <Alert variant="warning" isInline title="Export capped at 5000 rows" className="pf-v6-u-mb-sm">
            The period returned more movements than the cap. Narrow the period (or filter by organization) to get a
            complete export.
          </Alert>
        )}
        <Table aria-label="Wallet ledger export" variant="compact">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Organization</Th>
              <Th>Kind</Th>
              <Th>Description</Th>
              <Th>Reference</Th>
              <Th className="pf-v6-u-text-align-end">Amount</Th>
              <Th className="pf-v6-u-text-align-end">Balance after</Th>
              <Th>By</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginated.map((t) => (
              <Tr key={t.id}>
                <Td dataLabel="Date">{formatDateTime(t.created_at)}</Td>
                <Td dataLabel="Organization">{t.client_name ?? `Client #${t.client_id}`}</Td>
                <Td dataLabel="Kind">
                  <TransactionKindLabel kind={t.kind} amount={t.amount} />
                </Td>
                <Td dataLabel="Description">{t.description}</Td>
                <Td dataLabel="Reference">{t.reference_label ?? '—'}</Td>
                <Td dataLabel="Amount" className="pf-v6-u-text-align-end">
                  <SignedAmount value={t.amount} />
                </Td>
                <Td dataLabel="Balance after" className="pf-v6-u-text-align-end">
                  {formatPrice(t.balance_after)}
                </Td>
                <Td dataLabel="By">{t.user_id ?? '—'}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {pagination('bottom')}
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <DatePicker
              value={fromInput}
              onChange={(_e, v) => setFromInput(v)}
              dateParse={parseStrictIsoDate}
              aria-label="Period start date"
              buttonAriaLabel="Toggle period start calendar"
              placeholder="From (YYYY-MM-DD)"
            />
          </ToolbarItem>
          <ToolbarItem>
            <DatePicker
              value={toInput}
              onChange={(_e, v) => setToInput(v)}
              dateParse={parseStrictIsoDate}
              aria-label="Period end date"
              buttonAriaLabel="Toggle period end calendar"
              placeholder="To (YYYY-MM-DD)"
              rangeStart={fromCandidate ? parseStrictIsoDate(fromCandidate) : undefined}
              validators={[
                (date) =>
                  fromCandidate && date < parseStrictIsoDate(fromCandidate) ? 'Must be on or after the from date' : '',
              ]}
            />
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isClientOpen}
              selected={clientId ?? 'all'}
              onSelect={(_e, value) => {
                setClientId(typeof value === 'number' ? value : undefined);
                setIsClientOpen(false);
              }}
              onOpenChange={setIsClientOpen}
              isScrollable
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsClientOpen(!isClientOpen)}
                  isExpanded={isClientOpen}
                  aria-label="Filter by organization"
                >
                  {clientLabel}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All organizations</SelectOption>
                {wallets.map((w) => (
                  <SelectOption key={w.client_id} value={w.client_id}>
                    {w.client_name}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Select
              isOpen={isKindOpen}
              selected={kind ?? 'all'}
              onSelect={(_e, value) => {
                setKind(isKind(value) ? value : undefined);
                setIsKindOpen(false);
              }}
              onOpenChange={setIsKindOpen}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsKindOpen(!isKindOpen)}
                  isExpanded={isKindOpen}
                  aria-label="Filter by movement kind"
                >
                  {kindLabel}
                </MenuToggle>
              )}
            >
              <SelectList>
                <SelectOption value="all">All kinds</SelectOption>
                {KINDS.map((k) => (
                  <SelectOption key={k} value={k}>
                    {transactionKindText(k)}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <Button
              variant="secondary"
              icon={<DownloadIcon />}
              onClick={downloadCsv}
              isDisabled={transactions.length === 0 || loading}
            >
              Download CSV
            </Button>
          </ToolbarItem>
          <ToolbarItem variant="pagination">{pagination()}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default WalletsExportTab;
